import { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppShell, { SettingsGroup, SettingsGroupLabel } from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import { clearSearchHistory, requestDataDownload } from '../services/api';
import { useT } from '../i18n/useT';

function Row({
  title,
  subtitle,
  onClick,
  danger,
}: {
  title: string;
  subtitle?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        border: 0,
        borderBottom: `1px solid ${DesignSystem.colors.separator}`,
        background: 'transparent',
        textAlign: 'left',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        font: 'inherit',
        '&:last-child': { borderBottom: 0 },
        '&:hover': {
          backgroundColor: danger ? DesignSystem.colors.errorSoft : DesignSystem.colors.primaryLight,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: danger ? DesignSystem.colors.error : DesignSystem.colors.textPrimary }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary }}>{subtitle}</Typography>
        )}
      </Box>
      <ChevronRight sx={{ color: DesignSystem.colors.textSecondary, fontSize: 20 }} />
    </Box>
  );
}

export default function PrivacyCenterPage() {
  useThemeMode();
  const t = useT();
  const backendUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    if (!user?.token) return;
    setError(null);
    try {
      const res = await requestDataDownload(user.token, getApiLanguage());
      setMessage(res.message || t('privacyDownloadSuccess'));
    } catch {
      setError(t('privacyDownloadFailed'));
    }
  };

  const clearHistory = async () => {
    if (!user?.token) return;
    setError(null);
    try {
      await clearSearchHistory(user.token, getApiLanguage());
      setMessage(t('privacyHistoryCleared'));
    } catch {
      setError(t('privacyClearHistoryFailed'));
    }
  };

  return (
    <AppShell title={t('privacyTitle')} showBackButton maxWidth="sm" showCompanion={false}>
      <Typography sx={{ mb: 2, fontSize: 14, color: DesignSystem.colors.textSecondary }}>
        {t('privacyIntro')}
      </Typography>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <SettingsGroupLabel>{t('privacyGroupYourData')}</SettingsGroupLabel>
      <SettingsGroup>
        <Row title={t('privacyDownloadData')} subtitle={t('privacyDownloadDataSub')} onClick={() => void download()} />
        <Row title={t('privacyClearHistory')} subtitle={t('privacyClearHistorySub')} onClick={() => void clearHistory()} />
      </SettingsGroup>

      <SettingsGroupLabel>{t('privacyGroupLegal')}</SettingsGroupLabel>
      <SettingsGroup>
        <Row title={t('privacyPolicyTitle')} onClick={() => window.open(`${backendUrl}/privacypolicy`, '_blank')} />
        <Row title={t('privacyTermsOfService')} onClick={() => window.open(`${backendUrl}/terms`, '_blank')} />
      </SettingsGroup>

      <SettingsGroupLabel>{t('privacyGroupAccount')}</SettingsGroupLabel>
      <SettingsGroup>
        <Row title={t('deleteAccount')} subtitle={t('privacyDeleteAccountSub')} onClick={() => navigate('/profile')} danger />
        <Row
          title={t('privacyLogoutDevice')}
          onClick={async () => {
            await logout();
            navigate('/?login=1');
          }}
          danger
        />
      </SettingsGroup>
    </AppShell>
  );
}
